import React, {useState, useEffect} from 'react'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  GetApp as DownloadIcon,
  Info as InfoIcon,
  Archive as ArchiveIcon,
  Edit as EditIcon
} from '@mui/icons-material'
import { config } from '../config/environment'

interface FeedbackAttachment {
  url?: string
  s3_key?: string
  name?: string
  size?: number
  type?: string
}

interface FeedbackItem {
  id: number
  user_id: string
  user_email: string
  user_department: string
  tool_context: string
  message: string
  attachments?: string | FeedbackAttachment[]
  status: 'open' | 'in-progress' | 'resolved' | 'closed'
  priority: 'low' | 'normal' | 'high' | 'critical'
  created_at: number
  updated_at: number
  user_agent?: string
  ip_address?: string
}

interface FeedbackResponse {
  success: boolean
  feedback: FeedbackItem[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export const FeedbackAdminViewer: React.FC = () => {
  const [feedbackData, setFeedbackData] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [toolFilter, setToolFilter] = useState<string>('all')
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(
    null
  )
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingFeedback, setEditingFeedback] = useState<FeedbackItem | null>(
    null
  )
  const [editFormData, setEditFormData] = useState<{
    status: 'open' | 'in-progress' | 'resolved' | 'closed'
    priority: 'low' | 'normal' | 'high' | 'critical'
  }>({
    status: 'open',
    priority: 'normal'
  })

  // Get base URL from config
  const baseUrl = config.API_BASE_URL

  const fetchFeedback = async () => {
    setLoading(true)
    setError(null)

    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      })

      if (statusFilter !== 'all') {
        params.append('status', statusFilter)
      }
      if (toolFilter !== 'all') {
        params.append('toolName', toolFilter)
      }

      const response = await fetch(`${baseUrl}/api/feedback?${params}`, { credentials: 'include' })
      const data: FeedbackResponse = await response.json()

      if (data.success) {
        setFeedbackData(data.feedback)
      } else {
        setError('Failed to fetch feedback data')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch feedback')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFeedback()
  }, [page, rowsPerPage, statusFilter, toolFilter])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'error'
      case 'in-progress':
        return 'warning'
      case 'resolved':
        return 'success'
      case 'closed':
        return 'default'
      default:
        return 'default'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'error'
      case 'high':
        return 'warning'
      case 'normal':
        return 'info'
      case 'low':
        return 'success'
      default:
        return 'default'
    }
  }

  const parseAttachments = (
    attachments: string | FeedbackAttachment[] | undefined
  ): FeedbackAttachment[] => {
    if (!attachments) return []

    if (typeof attachments === 'string') {
      try {
        const parsed = JSON.parse(attachments)
        if (Array.isArray(parsed)) {
          // Handle both old format (array of URLs) and new format (array of objects)
          return parsed.map((item) => {
            if (typeof item === 'string') {
              // Old format: extract s3_key from URL
              const urlObj = new URL(item)
              return {
                url: item,
                s3_key: urlObj.pathname.substring(1), // Remove leading slash
                name: item.split('/').pop() || 'attachment',
                size: 0,
                type: 'unknown'
              }
            } else {
              // New format: already an object
              return item
            }
          })
        }
      } catch (e) {
        console.error('Failed to parse attachments:', e)
      }
    } else if (Array.isArray(attachments)) {
      return attachments
    }

    return []
  }

  const generateAttachmentDownloadUrl = async (
    s3Key: string,
    fileName: string
  ): Promise<{downloadUrl: string; fileName: string; expiresIn: number}> => {
    try {
      const response = await fetch(`${baseUrl}/api/feedback/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          s3Key,
          expiresIn: 3600, // 1 hour
          download: true // Request download headers
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(
          '❌ Pre-signed URL API error:',
          response.status,
          errorText
        )
        throw new Error(`API request failed: ${response.status} ${errorText}`)
      }

      const data = await response.json()

      if (data.success) {
        return {
          downloadUrl: data.presignedUrl,
          fileName: fileName,
          expiresIn: 3600
        }
      } else {
        throw new Error(data.error || 'Failed to generate download URL')
      }
    } catch (error) {
      console.error('❌ Error generating attachment download URL:', error)
      throw error
    }
  }

  const generatePresignedUrl = async (s3Key: string): Promise<string> => {
    try {
      const response = await fetch(`${baseUrl}/api/feedback/presigned-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          s3Key,
          expiresIn: 3600, // 1 hour
          download: false // For viewing, not downloading
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error(
          '❌ Pre-signed URL API error:',
          response.status,
          errorText
        )
        throw new Error(`API request failed: ${response.status} ${errorText}`)
      }

      const data = await response.json()

      if (data.success) {
        return data.presignedUrl
      } else {
        throw new Error(data.error || 'Failed to generate presigned URL')
      }
    } catch (error) {
      console.error('❌ Error generating presigned URL:', error)
      throw error
    }
  }

  const handleDownloadAttachment = async (attachment: FeedbackAttachment) => {
    if (!attachment.s3_key) {
      alert('No S3 key available for this attachment')
      return
    }

    try {
      // Use the same pattern as Dashboard template downloads
      const downloadInfo = await generateAttachmentDownloadUrl(
        attachment.s3_key,
        attachment.name || 'attachment'
      )

      // Create a temporary link and click it to trigger download
      const link = document.createElement('a')
      link.href = downloadInfo.downloadUrl
      link.download = downloadInfo.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('❌ Error downloading attachment:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      alert(`Failed to download attachment: ${errorMessage}`)
    }
  }

  const handleDownloadAll = async (
    _feedbackId: number,
    attachments: FeedbackAttachment[]
  ) => {
    if (attachments.length === 0) return

    try {
      // For now, download each attachment individually using the same pattern as templates
      // TODO: Implement backend ZIP creation for bulk download
      for (let i = 0; i < attachments.length; i++) {
        const attachment = attachments[i]
        if (attachment.s3_key) {
          try {
            const downloadInfo = await generateAttachmentDownloadUrl(
              attachment.s3_key,
              attachment.name || `attachment-${attachment.s3_key}`
            )

            // Create a temporary link and click it to trigger download
            const link = document.createElement('a')
            link.href = downloadInfo.downloadUrl
            link.download = downloadInfo.fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)

            // Add small delay between downloads to avoid overwhelming the browser
            if (i < attachments.length - 1) {
              await new Promise((resolve) => setTimeout(resolve, 1000))
            }
          } catch (attachmentError) {
            console.error(
              `❌ Failed to download ${attachment.name}:`,
              attachmentError
            )
            // Continue with other attachments even if one fails
          }
        }
      }
    } catch (error) {
      console.error('❌ Error downloading attachments:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      alert(`Failed to download attachments: ${errorMessage}`)
    }
  }

  const handleViewAttachment = async (attachment: FeedbackAttachment) => {
    if (!attachment.s3_key) {
      alert('No S3 key available for this attachment')
      return
    }

    try {
      const presignedUrl = await generatePresignedUrl(attachment.s3_key)
      window.open(presignedUrl, '_blank')
    } catch (error) {
      console.error('❌ Error viewing attachment:', error)
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      alert(`Failed to open attachment: ${errorMessage}`)
    }
  }

  const handleViewDetails = (feedback: FeedbackItem) => {
    setSelectedFeedback(feedback)
    setDetailDialogOpen(true)
  }

  const handleEditFeedback = (feedback: FeedbackItem) => {
    setEditingFeedback(feedback)
    setEditFormData({
      status: feedback.status,
      priority: feedback.priority
    })
    setEditDialogOpen(true)
  }

  const handleSaveFeedbackChanges = async () => {
    if (!editingFeedback) return

    try {
      const response = await fetch(
        `${baseUrl}/api/feedback/${editingFeedback.id}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(editFormData)
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(
          `Failed to update feedback: ${response.status} - ${errorText}`
        )
      }

      const result = await response.json()

      if (result.success) {
        // Update the local data
        setFeedbackData((prevData) =>
          prevData.map((item) =>
            item.id === editingFeedback.id
              ? {
                  ...item,
                  ...editFormData,
                  updated_at: Math.floor(Date.now() / 1000)
                }
              : item
          )
        )

        setEditDialogOpen(false)
        setEditingFeedback(null)
      } else {
        throw new Error(result.error || 'Failed to update feedback')
      }
    } catch (error) {
      console.error('Error updating feedback:', error)
      alert(
        `Failed to update feedback: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  const uniqueTools = [
    ...new Set(feedbackData.map((item) => item.tool_context).filter(Boolean))
  ]

  if (loading && feedbackData.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
        <Typography sx={{ml: 2}}>Loading feedback data...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          Feedback Management
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchFeedback}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{mb: 3}}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small" sx={{minWidth: 120}}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="in-progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{minWidth: 120}}>
          <InputLabel>Tool</InputLabel>
          <Select
            value={toolFilter}
            label="Tool"
            onChange={(e) => setToolFilter(e.target.value)}
          >
            <MenuItem value="all">All Tools</MenuItem>
            {uniqueTools.map((tool) => (
              <MenuItem key={tool} value={tool}>
                {tool}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Feedback Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Tool</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Attachments</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedbackData.map((feedback) => {
              const attachments = parseAttachments(feedback.attachments)

              return (
                <TableRow key={feedback.id}>
                  <TableCell>{feedback.id}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {feedback.user_email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {feedback.user_department}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feedback.tool_context || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{maxWidth: 200}}>
                    <Typography variant="body2" noWrap>
                      {feedback.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {attachments.length > 0 ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        {attachments.map((attachment, index) => (
                          <Tooltip
                            key={index}
                            title={`Download ${
                              attachment.name || 'attachment'
                            }`}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadAttachment(attachment)
                              }
                              color="primary"
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ))}
                        <Typography variant="caption" color="text.secondary">
                          ({attachments.length})
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feedback.status}
                      color={getStatusColor(feedback.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={feedback.priority}
                      color={getPriorityColor(feedback.priority) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {formatDate(feedback.created_at)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(feedback)}
                          color="info"
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status & Priority">
                        <IconButton
                          size="small"
                          onClick={() => handleEditFeedback(feedback)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {attachments.length > 0 && (
                        <Tooltip title="Download All Attachments">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownloadAll(feedback.id, attachments)
                            }
                            color="secondary"
                          >
                            <ArchiveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={feedbackData.length} // Note: This should ideally come from the API pagination
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Feedback Details #{selectedFeedback?.id}</DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Typography>
                <strong>Email:</strong> {selectedFeedback.user_email}
              </Typography>
              <Typography>
                <strong>Department:</strong> {selectedFeedback.user_department}
              </Typography>
              <Typography>
                <strong>Tool:</strong> {selectedFeedback.tool_context}
              </Typography>

              <Typography variant="h6" gutterBottom sx={{mt: 3}}>
                Feedback
              </Typography>
              <Typography>{selectedFeedback.message}</Typography>

              <Typography variant="h6" gutterBottom sx={{mt: 3}}>
                Attachments
              </Typography>
              {parseAttachments(selectedFeedback.attachments).length > 0 ? (
                <Stack spacing={1}>
                  {parseAttachments(selectedFeedback.attachments).map(
                    (attachment, index) => (
                      <Box
                        key={index}
                        display="flex"
                        alignItems="center"
                        gap={1}
                        p={1}
                        border={1}
                        borderColor="divider"
                        borderRadius={1}
                      >
                        <Typography flex={1}>{attachment.name}</Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<ViewIcon />}
                          onClick={() => handleViewAttachment(attachment)}
                          sx={{mr: 1}}
                        >
                          View
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadAttachment(attachment)}
                        >
                          Download
                        </Button>
                      </Box>
                    )
                  )}
                  {parseAttachments(selectedFeedback.attachments).length >
                    1 && (
                    <Button
                      variant="outlined"
                      startIcon={<ArchiveIcon />}
                      onClick={() =>
                        handleDownloadAll(
                          selectedFeedback.id,
                          parseAttachments(selectedFeedback.attachments)
                        )
                      }
                      sx={{mt: 1}}
                    >
                      Download All Attachments
                    </Button>
                  )}
                </Stack>
              ) : (
                <Typography color="text.secondary">No attachments</Typography>
              )}

              <Typography variant="h6" gutterBottom sx={{mt: 3}}>
                Metadata
              </Typography>
              <Typography>
                <strong>Status:</strong> {selectedFeedback.status}
              </Typography>
              <Typography>
                <strong>Priority:</strong> {selectedFeedback.priority}
              </Typography>
              <Typography>
                <strong>Created:</strong>{' '}
                {formatDate(selectedFeedback.created_at)}
              </Typography>
              <Typography>
                <strong>Updated:</strong>{' '}
                {formatDate(selectedFeedback.updated_at)}
              </Typography>
              {selectedFeedback.user_agent && (
                <Typography>
                  <strong>User Agent:</strong> {selectedFeedback.user_agent}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Feedback Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Feedback #{editingFeedback?.id}</DialogTitle>
        <DialogContent>
          {editingFeedback && (
            <Box sx={{pt: 2}}>
              <Typography variant="h6" gutterBottom>
                User Information
              </Typography>
              <Typography sx={{mb: 1}}>
                <strong>Email:</strong> {editingFeedback.user_email}
              </Typography>
              <Typography sx={{mb: 1}}>
                <strong>Department:</strong> {editingFeedback.user_department}
              </Typography>
              <Typography sx={{mb: 3}}>
                <strong>Tool:</strong> {editingFeedback.tool_context}
              </Typography>

              <Typography variant="h6" gutterBottom>
                Feedback Message
              </Typography>
              <Typography
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 0, 0, 0.12)'
                }}
              >
                {editingFeedback.message}
              </Typography>

              <Typography variant="h6" gutterBottom>
                Update Status & Priority
              </Typography>

              <Box sx={{display: 'flex', gap: 2, mt: 2}}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editFormData.status}
                    label="Status"
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        status: e.target.value as typeof prev.status
                      }))
                    }
                  >
                    <MenuItem value="open">Open</MenuItem>
                    <MenuItem value="in-progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="closed">Closed</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={editFormData.priority}
                    label="Priority"
                    onChange={(e) =>
                      setEditFormData((prev) => ({
                        ...prev,
                        priority: e.target.value as typeof prev.priority
                      }))
                    }
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: 'rgba(25, 118, 210, 0.08)',
                  borderRadius: 1,
                  border: '1px solid rgba(25, 118, 210, 0.2)'
                }}
              >
                <Typography variant="body2" sx={{color: 'primary.dark'}}>
                  <strong>Current:</strong> Status: {editingFeedback.status} |
                  Priority: {editingFeedback.priority}
                </Typography>
                <Typography variant="body2" sx={{color: 'primary.dark'}}>
                  <strong>New:</strong> Status: {editFormData.status} |
                  Priority: {editFormData.priority}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveFeedbackChanges}
            variant="contained"
            disabled={
              !editingFeedback ||
              (editFormData.status === editingFeedback.status &&
                editFormData.priority === editingFeedback.priority)
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default FeedbackAdminViewer
